import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Settings, BookOpen, Globe2, Save, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { tafsirApi } from '../services/api';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading } from '../components/shared';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading, updatePreferences } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState('fr');
  const [selectedSlugs, setSelectedSlugs] = useState([]);

  const LANGUAGES = [
    { code: 'fr', label: 'Français', description: t('settings.lang_fr_desc') },
    { code: 'ar', label: 'العربية', description: t('settings.lang_ar_desc') },
    { code: 'en', label: 'English', description: t('settings.lang_en_desc') },
  ];

  useEffect(() => {
    tafsirApi.getSources().then((r) => {
      setSources(r.data.sources || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const prefs = user?.preferences?.tafsir;
    if (prefs) {
      if (prefs.default_language) setLang(prefs.default_language);
      if (Array.isArray(prefs.preferred_source_slugs)) {
        setSelectedSlugs(prefs.preferred_source_slugs);
      }
    }
  }, [user]);

  if (authLoading) return <PageLoading />;
  if (!user) return <Navigate to="/login" state={{ from: { pathname: '/settings' } }} replace />;

  const toggleSource = (slug) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await updatePreferences({
      default_language: lang,
      preferred_source_slugs: selectedSlugs,
    });
    setSaving(false);
    if (res.success) {
      toast.success(t('settings.preferences_saved'));
      if (Array.isArray(res.tafsir?.preferred_source_slugs)) {
        setSelectedSlugs(res.tafsir.preferred_source_slugs);
      }
    } else {
      toast.error(res.error || t('common.save_failed'));
    }
  };

  const sourcesByLanguage = sources.reduce((acc, s) => {
    (acc[s.language_code] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen py-12 px-4" data-testid="settings-page">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="overline mb-2 block">{t('settings.your_profile')}</span>
          <h1 className="text-3xl font-serif text-text-primary flex items-center gap-3">
            <Settings className="w-7 h-7 text-primary" />
            {t('settings.study_preferences')}
          </h1>
          <p className="text-text-secondary mt-2">
            {t('settings.study_help')}
          </p>
        </motion.div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            <GlassCard data-testid="settings-language">
              <h2 className="flex items-center gap-2 font-serif text-lg text-text-primary mb-4">
                <Globe2 className="w-5 h-5 text-primary" />
                {t('settings.default_tafsir_language')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`text-left p-4 rounded-lg border transition-all ${
                      lang === l.code
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-surface-elevated text-text-secondary hover:border-primary/40'
                    }`}
                    data-testid={`settings-lang-${l.code}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-base">{l.label}</span>
                      {lang === l.code && <Check className="w-4 h-4" />}
                    </div>
                    <p className="text-xs text-text-muted">{l.description}</p>
                  </button>
                ))}
              </div>
            </GlassCard>

            <GlassCard data-testid="settings-sources">
              <h2 className="flex items-center gap-2 font-serif text-lg text-text-primary mb-1">
                <BookOpen className="w-5 h-5 text-primary" />
                {t('settings.preferred_sources')}
              </h2>
              <p className="text-sm text-text-muted mb-4">
                {t('settings.preferred_sources_help')}
              </p>
              <div className="space-y-4">
                {LANGUAGES.filter((l) => sourcesByLanguage[l.code]?.length).map((l) => (
                  <div key={l.code}>
                    <p className="text-xs uppercase tracking-wider text-text-muted mb-2">
                      {l.label}
                    </p>
                    <div className="space-y-2">
                      {sourcesByLanguage[l.code].map((s) => {
                        const active = selectedSlugs.includes(s.slug);
                        return (
                          <label
                            key={s.slug}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              active
                                ? 'border-primary/50 bg-primary/5'
                                : 'border-border bg-surface-elevated hover:border-primary/30'
                            }`}
                            data-testid={`settings-source-${s.slug}`}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => toggleSource(s.slug)}
                              className="mt-1 w-4 h-4 accent-primary"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-text-primary">
                                {s.display_name_localized?.[lang] || s.display_name}
                              </p>
                              <p className="text-xs text-text-muted mt-0.5">
                                {s.author}
                                {' · '}
                                {t('settings.entries_count', { count: s.records_count.toLocaleString() })}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            <div className="flex justify-between items-center pt-4 border-t border-border">
              <Link
                to="/settings/audio"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
                data-testid="settings-audio-link"
              >
                {t('settings.audio_link')}
              </Link>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary text-background hover:bg-primary-hover"
                data-testid="settings-save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t('settings.save_preferences')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
